/**
 * Gera e descarrega um .xlsx (uma ou várias folhas), montado à mão com o jszip (o
 * mesmo formato das exportações de exportacoesView e GenericDataTable2).
 *
 * Cada coluna é `{ label, get(linha) }`. Um `get` que devolva um number fica
 * como célula numérica — dá para somar, ordenar e usar em fórmulas no Excel;
 * tudo o resto segue como texto.
 */
import jszip from "jszip";

// Caracteres de controlo que o XML 1.0 não aceita: basta um deles numa morada
// para o Excel recusar abrir o ficheiro.
const CONTROLO_INVALIDO = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g;

const escapeXml = (s) =>
  String(s ?? "")
    .replace(CONTROLO_INVALIDO, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const xlsxCellRef = (col, row) => {
  let s = "";
  let c = col;
  do {
    s = String.fromCharCode(65 + (c % 26)) + s;
    c = Math.floor(c / 26) - 1;
  } while (c >= 0);
  return s + (row + 1);
};

const celula = (ref, valor) =>
  typeof valor === "number" && Number.isFinite(valor)
    ? `<c r="${ref}"><v>${valor}</v></c>`
    : `<c r="${ref}" t="inlineStr"><is><t>${escapeXml(valor)}</t></is></c>`;

function folhaXml(linhas, colunas) {
  // Largura de cada coluna = texto mais comprido que lá cai (com teto), para a
  // folha abrir legível em vez de tudo cortado à largura por defeito.
  const larguras = colunas.map((col) => String(col.label).length);

  let sheetData = '<sheetData><row r="1">';
  colunas.forEach((col, ci) => {
    sheetData += celula(xlsxCellRef(ci, 0), String(col.label));
  });
  sheetData += "</row>";

  linhas.forEach((linha, ri) => {
    sheetData += `<row r="${ri + 2}">`;
    colunas.forEach((col, ci) => {
      const valor = col.get(linha);
      larguras[ci] = Math.max(larguras[ci], String(valor ?? "").length);
      sheetData += celula(xlsxCellRef(ci, ri + 1), valor);
    });
    sheetData += "</row>";
  });
  sheetData += "</sheetData>";

  const cols = larguras
    .map(
      (largura, i) =>
        `<col min="${i + 1}" max="${i + 1}" width="${Math.min(largura + 2, 60)}" customWidth="1"/>`
    )
    .join("");

  const lastRef = xlsxCellRef(colunas.length - 1, linhas.length);
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <dimension ref="A1:${lastRef}"/>
  <cols>${cols}</cols>
  ${sheetData}
</worksheet>`;
}

// Nome de folha válido no Excel: até 31 carateres e sem : \ / ? * [ ]
const nomeDeFolha = (nome) => String(nome || "Folha").replace(/[:\\/?*[\]]/g, " ").slice(0, 31);

/** folhas: [{ nome, linhas, colunas }] */
export async function gerarXlsxFolhas(folhas) {
  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>${folhas
    .map((f, i) => `<sheet name="${escapeXml(nomeDeFolha(f.nome))}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
    .join("")}</sheets>
</workbook>`;

  const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${folhas
    .map(
      (_, i) =>
        `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`
    )
    .join("")}
</Relationships>`;

  const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  ${folhas
    .map(
      (_, i) =>
        `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
    )
    .join("")}
</Types>`;

  const topRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

  const zip = new jszip();
  zip.file("[Content_Types].xml", contentTypesXml);
  zip.file("_rels/.rels", topRelsXml);
  zip.file("xl/workbook.xml", workbookXml);
  zip.file("xl/_rels/workbook.xml.rels", relsXml);
  folhas.forEach((f, i) => zip.file(`xl/worksheets/sheet${i + 1}.xml`, folhaXml(f.linhas, f.colunas)));

  return zip.generateAsync({
    type: "blob",
    mimeType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export async function gerarXlsx(linhas, colunas, nomeFolha = "Export") {
  return gerarXlsxFolhas([{ nome: nomeFolha, linhas, colunas }]);
}

export async function descarregarXlsx(linhas, colunas, nomeFicheiro, nomeFolha) {
  await descarregarXlsxFolhas([{ nome: nomeFolha, linhas, colunas }], nomeFicheiro);
}

export async function descarregarXlsxFolhas(folhas, nomeFicheiro) {
  const blob = await gerarXlsxFolhas(folhas);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeFicheiro;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
